import {
  Alert,
  Heading,
  IconAnnotateLine,
  IconCompleteLine,
  IconExternalLinkLine,
  IconHourGlassLine,
  IconImportLine,
  ScreenReaderContent,
  Table,
  Text,
  View,
} from "@instructure/ui";
import { useContext } from "react";
import DataGrid from "react-data-grid";
import { useTranslation } from "react-i18next";
import { TermsContext } from "../../../utils/Context";
import { TruncateToolTip } from "../../../utils/TruncateTooltip";
import { sort } from "../../../utils/utils";
import { sortOutcomes } from "../../LearningOutcomes/utils";

const PlanDataGrid = ({ plan, assignmentTypes, subjects, courseLevels }) => {
  const { t } = useTranslation();
  const { canvasTerms, insightsTerms } = useContext(TermsContext);
  const targetedAssessments = plan.sections
    .map((section) =>
      section.assessments.filter(
        (assessment) => assessment.assessment_type !== "flexible"
      )
    )
    .flat();
  const flexibleAssessments = plan.sections
    .map((section) =>
      section.assessments.filter(
        (assessment) => assessment.assessment_type === "flexible"
      )
    )
    .flat();

  const initializeColumns = () => {
    return [
      {
        key: "subject",
        name: (
          <ScreenReaderContent>
            {t("assessment.courseName")}
          </ScreenReaderContent>
        ),
        frozen: true,
        resizable: true,
        width: 150,
        maxWidth: 150,
      },
      {
        key: "los",
        name: (
          <TruncateToolTip renderTip={t("assessment.loTargets")}>
            {t("assessment.loTargets")}
          </TruncateToolTip>
        ),
        frozen: true,
        resizable: true,
        width: 200,
        maxWidth: 200,
      },
      {
        key: "assignments",
        name: (
          <TruncateToolTip renderTip={t("assessment.assignmentType")}>
            {t("assessment.assignmentType")}
          </TruncateToolTip>
        ),
        frozen: true,
        resizable: true,
        width: 150,
        maxWidth: 150,
      },
    ];
  };

  const fetchRelevantTerms = () => {
    const terms = [];
    targetedAssessments.forEach((assessment) => {
      if (assessment.terms) {
        assessment.terms.forEach((term) => {
          const newTerm = {
            key: term.ddl_id,
            name: (
              <TruncateToolTip renderTip={term.name}>
                {term.name}
              </TruncateToolTip>
            ),
            id: term.ddl_id,
            start_at: term.start_at,
            minWidth: 200,
          };
          if (
            !terms.some(
              (term) =>
                Object.entries(term).toString() ===
                Object.entries(newTerm).toString()
            )
          ) {
            terms.push(newTerm);
          }
        });
      }
      if (assessment.assessment_type === "external") {
        const term = canvasTerms.find(
          (t) => t.id === assessment.assessing_terms[0]
        );
        const newTerm = {
          key: term.id,
          name: (
            <TruncateToolTip renderTip={term.name}>{term.name}</TruncateToolTip>
          ),
          id: term.id,
          start_at: term.start_at,
          minWidth: 200,
        };
        if (
          !terms.some(
            (term) =>
              Object.entries(term).toString() ===
              Object.entries(newTerm).toString()
          )
        ) {
          terms.push(newTerm);
        }
      }
    });

    terms.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    return terms;
  };

  const findAssignmentType = (assignment_type) => {
    if (assignment_type) {
      const assignType = assignmentTypes.find(
        (obj) => obj.id === assignment_type
      );
      return assignType.name;
    } else {
      return t("global.na");
    }
  };

  const fillAssessmentInfo = (offering, los, assignmentType) => {
    los = sortOutcomes(los);
    const loList = (
      <Text>
        {los.map((lo, key) => (
          <Text as="div" size="small" key={key}>
            {lo.name}
          </Text>
        ))}
      </Text>
    );

    return {
      subject: (
        <TruncateToolTip
          renderTip={<div style={{ maxWidth: 400 }}>{offering.code}</div>}
        >
          {offering.code}
        </TruncateToolTip>
      ),
      los: (
        <div
          style={{
            lineHeight: 1.5,
            padding: "5px",
            height: "100%",
            whiteSpace: "normal",
          }}
        >
          <TruncateToolTip
            renderTip={<div style={{ maxWidth: 400 }}>{loList}</div>}
            maxLines={2}
          >
            {loList}
          </TruncateToolTip>
        </div>
      ),
      assignments: (
        <div
          style={{
            lineHeight: 1.5,
            padding: "5px",
            height: "100%",
            whiteSpace: "normal",
          }}
        >
          <TruncateToolTip
            renderTip={<div style={{ maxWidth: 400 }}>{assignmentType}</div>}
            maxLines={2}
          >
            {assignmentType}
          </TruncateToolTip>
        </div>
      ),
    };
  };

  const CanvasAssessmentCell = ({ status }) => {
    return (
      <div
        style={{
          width: "auto",
          height: "100%",
          textAlign: "center",
          color: status === "progress" ? "#000" : "#fff",
          marginLeft: "-8px",
          marginRight: "-8px",
          backgroundColor:
            (status === "planned" && "#287A9F") ||
            (status === "deployed" && "#BF32A4") ||
            (status === "progress" && "#ceac12") ||
            (status === "completed" && "#126417") ||
            (status === "n/a" && "#595959"),
        }}
      >
        {status === "n/a" && <PlanGridCell status={status} />}
        {status === "planned" && (
          <PlanGridCell
            status={status}
            icon={<IconAnnotateLine size="small" />}
          />
        )}
        {status === "deployed" && (
          <PlanGridCell
            status={status}
            icon={<IconImportLine size="small" />}
          />
        )}
        {status === "progress" && (
          <PlanGridCell
            status={status}
            icon={<IconHourGlassLine size="small" />}
          />
        )}
        {status === "completed" && (
          <PlanGridCell
            status={status}
            icon={<IconCompleteLine size="small" />}
          />
        )}
      </div>
    );
  };

  const ExternalAssessmentCell = ({ assessment }) => {
    return (
      <div
        style={{
          width: "auto",
          height: "100%",
          textAlign: "center",
          color: "#fff",
          marginLeft: "-8px",
          marginRight: "-8px",
          backgroundColor: "#67239f",
        }}
      >
        <PlanGridCell
          status={
            assessment.purl_type !== "juried"
              ? t("assessment.external")
              : t("assessment.externalJuriedAssessment")
          }
          icon={<IconExternalLinkLine size="small" />}
        />
      </div>
    );
  };

  const NotApplicableCell = () => {
    return (
      <div
        style={{
          width: "auto",
          height: "100%",
          textAlign: "center",
          color: "#fff",
          marginLeft: "-8px",
          marginRight: "-8px",
          backgroundColor: "#595959",
        }}
      >
        <PlanGridCell status={t("global.na")} />
      </div>
    );
  };

  // Initialize rows and columns
  const courseRows = [];
  let cols = initializeColumns();
  const termList = fetchRelevantTerms();
  cols = cols.concat(termList);
  const columns = cols;

  targetedAssessments.forEach((assessment) => {
    const { offerings, assignment_type, los } = assessment;
    if (offerings) {
      const assignmentType = findAssignmentType(assignment_type);
      // Create a row for each offering
      offerings.forEach((offering) => {
        // Fill in first 3 columns of the row (offering, learning outcomes, and assignment type)
        const row = fillAssessmentInfo(offering, los, assignmentType);
        // Fill in each cell with the appropriate status
        for (let i = 3; i < cols.length; i++) {
          if (Object.hasOwn(assessment.offering_to_terms_map, offering.id)) {
            const status = Object.hasOwn(
              assessment.offering_to_terms_map[offering.id],
              cols[i].id
            )
              ? assessment.offering_to_terms_map[offering.id][cols[i].id].status
              : "n/a";
            row[cols[i].key] = <CanvasAssessmentCell status={status} />;
          } else if (
            assessment.assessment_type === "external" &&
            cols[i].key === assessment.assessing_terms[0]
          ) {
            row[cols[i].key] = (
              <ExternalAssessmentCell assessment={assessment} />
            );
          } else {
            row[cols[i].key] = <NotApplicableCell />;
          }
        }
        courseRows.push(row);
      });
    }
  });

  const rows = courseRows;

  const getSortedTerms = (assessmentTerms) => {
    const uniqueTermIds = [
      ...new Set(assessmentTerms.map((obj) => obj.insights_term_id)),
    ];
    const uniqueTerms = uniqueTermIds.map((id) =>
      insightsTerms.find((term) => term.id === id)
    );
    uniqueTerms.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    return uniqueTerms;
  };

  const getSortedSubjects = (assessmentSubjects) => {
    let sortedSubjects = assessmentSubjects.map((subject) => {
      const subjectObj = subjects.find((s) => s.id === subject);
      return subjectObj.title;
    });
    sortedSubjects = sort(sortedSubjects);
    return sortedSubjects;
  };

  const getSortedCourseLevels = (assessmentCourseLevels) => {
    const levels = courseLevels.filter((level) =>
      assessmentCourseLevels.includes(level.id)
    );
    return levels;
  };

  return plan.sections.some((section) => section.assessments.length > 0) ? (
    <View as="div" margin="small">
      {flexibleAssessments.length > 0 && (
        <>
          <Heading level="h3" border="bottom">
            {t("assessment.flexibleAssessments")}
          </Heading>
          <View as="div" margin="small small medium small">
            <Table caption={t("assessment.flexibleAssessments")}>
              <Table.Head>
                <Table.Row>
                  <Table.ColHeader id="depts">
                    {t("global.departments")}
                  </Table.ColHeader>
                  <Table.ColHeader id="courseLevels">
                    {t("programs.courseLevels")}
                  </Table.ColHeader>
                  <Table.ColHeader id="collectionTerms">
                    {t("assessment.collectionTerms")}
                  </Table.ColHeader>
                  <Table.ColHeader id="learningOutcomeTargets">
                    {t("assessment.loTargets")}
                  </Table.ColHeader>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {flexibleAssessments.map((assessment, key) => (
                  <Table.Row key={key}>
                    <Table.Cell>
                      {getSortedSubjects(assessment.subjects).map(
                        (subject, key) => (
                          <Text as="div" key={key}>
                            {subject}
                          </Text>
                        )
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {getSortedCourseLevels(assessment.course_levels).map(
                        (level, key) => (
                          <Text as="div" key={key}>
                            {`${t("programs.courseLevel")} ${
                              level.courseLevel
                            } (${level.range.start} - ${level.range.end})`}
                          </Text>
                        )
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {getSortedTerms(assessment.terms).map((term, key) => (
                        <Text as="div" key={key}>
                          {term.name}
                        </Text>
                      ))}
                    </Table.Cell>
                    <Table.Cell>
                      {sortOutcomes(assessment.los).map((lo, key) => (
                        <Text as="div" key={key}>
                          {lo.name}
                        </Text>
                      ))}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </View>
        </>
      )}
      {targetedAssessments.length > 0 && (
        <>
          <Heading level="h3" margin="0 0 medium 0" border="bottom">
            {t("assessment.targetedAssessments")}
          </Heading>
          <DataGrid
            className="rdg-light"
            style={{ height: "auto", maxHeight: "400px" }}
            rowHeight={60}
            headerRowHeight={40}
            columns={columns}
            rows={rows}
          />
        </>
      )}
    </View>
  ) : (
    <div style={{ padding: 10 }}>
      <Alert variant="warning">{t("assessment.noResultsCollected")}</Alert>
    </div>
  );
};

const PlanGridCell = ({ status, icon }) => {
  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon && <span style={{ marginRight: 10 }}>{icon}</span>}
      <Text transform="capitalize" weight="bold" size="small">
        {status}
      </Text>
    </div>
  );
};

export default PlanDataGrid;
